import React, { useState, useCallback } from 'react';
import { Button, Input, Link } from '@cloudscape-design/components';
import { InputProps } from '@cloudscape-design/components/input';
import { NonCancelableCustomEvent } from '@cloudscape-design/components';
import { useCopilot } from './CopilotContext';
import './Copilot.scss';

export function CopilotMessageInput() {
  const { state, isResponding, sendMessage } = useCopilot();
  const [inputValue, setInputValue] = useState('');

  const handleSend = useCallback(() => {
    // Block while a turn is in progress so we never start a second concurrent stream —
    // this guards the Enter-key path too, not just the (disabled) button.
    if (inputValue.trim() && !isResponding) {
      sendMessage(inputValue);
      setInputValue('');
    }
  }, [inputValue, isResponding, sendMessage]);

  const recallLastSubmittedMessage = useCallback(() => {
    // Standard chat affordance: pressing Up in an empty input brings back the last
    // message the user submitted so it can be re-sent or edited. Only act when the
    // input is empty so we never overwrite something the user is mid-typing.
    if (inputValue) return;
    for (let i = state.messages.length - 1; i >= 0; i -= 1) {
      if (state.messages[i].role === 'user') {
        setInputValue(state.messages[i].content);
        return;
      }
    }
  }, [inputValue, state.messages]);

  const handleKeyDown = useCallback(
    (event: NonCancelableCustomEvent<InputProps.KeyDetail>) => {
      if (event.detail.key === 'Enter' && !event.detail.shiftKey) {
        handleSend();
      } else if (event.detail.key === 'ArrowUp') {
        recallLastSubmittedMessage();
      }
    },
    [handleSend, recallLastSubmittedMessage],
  );

  return (
    <>
      <div className="copilot-input-row">
        <div className="copilot-input-field">
          <Input
            value={inputValue}
            onChange={({ detail }) => setInputValue(detail.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about audits, tasks, questions..."
            disabled={isResponding}
            autoFocus
          />
        </div>
        <Button
          variant="primary"
          iconName="send"
          onClick={handleSend}
          disabled={isResponding || !inputValue.trim()}
          ariaLabel="Send message"
        />
      </div>
      <p className="copilot-input-disclaimer">
        Uses generative AI. Verify responses.{' '}
        <Link
          href="https://policy.a2z.com/docs/643450/publication"
          external
          fontSize="inherit"
          variant="primary"
        >
          Amazon AI Policy
        </Link>
      </p>
    </>
  );
}
